import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export type HostState =
  | { kind: 'active_host'; producerId: string; membershipId: string; onboardedAt: string | null }
  | { kind: 'interest_sent' }
  | { kind: 'not_host' };

export function useHostState() {
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['host-state', userId],
    enabled: !!userId && !authLoading,
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<HostState> => {
      if (!userId) return { kind: 'not_host' };

      // 1) producer_members (highest priority) — MUST filter by user_id
      const { data: membership, error: memErr } = await supabase
        .from('producer_members')
        .select('id, producer_id, onboarded_at')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();

      if (import.meta.env.DEV) {
        console.debug('[useHostState] producer_members', { userId, membership, memErr });
      }

      if (membership?.producer_id) {
        return {
          kind: 'active_host',
          producerId: membership.producer_id,
          membershipId: membership.id,
          onboardedAt: membership.onboarded_at ?? null,
        };
      }

      // 2) producer_interest
      const { data: interest, error: intErr } = await supabase
        .from('producer_interest')
        .select('id, status')
        .eq('user_id', userId)
        .in('status', ['new', 'pending', 'submitted', 'reviewed'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (import.meta.env.DEV) {
        console.debug('[useHostState] producer_interest', { userId, interest, intErr });
      }

      if (interest?.id) return { kind: 'interest_sent' };

      return { kind: 'not_host' };
    },
  });

  return {
    hostState: (data ?? { kind: 'not_host' }) as HostState,
    isLoading: authLoading || queryLoading,
  };
}
